import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

function TermsOfServiceMain() {
    const { setCurrentPage } = useOutletContext();

    useEffect(() => {
        setCurrentPage("Terms of Service");
    }, [setCurrentPage]);

    const sections = [
        { id: 'acceptance', label: 'Acceptance of Terms', icon: 'gavel' },
        { id: 'user-accounts', label: 'User Accounts', icon: 'account_circle' },
        { id: 'license', label: 'Telemetry & License', icon: 'terminal' },
        { id: 'payments', label: 'Subscription & Payments', icon: 'payments' },
        { id: 'conduct', label: 'Prohibited Conduct', icon: 'block' },
        { id: 'liability', label: 'Limitation of Liability', icon: 'warning' },
    ];

    const [activeSection, setActiveSection] = useState(sections[0].id);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0
        };

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
            if (entry.isIntersecting) {
                setActiveSection(entry.target.id);
            }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        sections.forEach((section) => {
            const element = document.getElementById(section.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);

        if (element) {
            const offset = 80;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main className="relative flex min-h-[calc(100vh-64px)] w-full carbon-bg">
            <div className="layout-container max-w-[1440px] mx-auto flex w-full">
                <aside className="hidden lg:flex flex-col w-80 border-r border-border-color p-8 sticky top-16 h-full">
                    <div className="flex flex-col gap-6 sticky top-[140px]">
                        <div className="mb-8">
                            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4">Agreement Sections</h3>
                            <nav className="flex flex-col gap-1">
                                {sections.map((section) => {
                                    const isActive = activeSection === section.id;
                                    
                                    return (
                                    <button
                                        key={section.id}
                                        onClick={() => scrollToSection(section.id)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all border ${
                                        isActive
                                            ? "bg-primary/10 text-primary border-primary/20 font-bold"
                                            : "hover:bg-surface text-slate-400 hover:text-white border-transparent"
                                        }`}
                                    >
                                        <span className="material-symbols-outlined text-xl">
                                        {section.icon}
                                        </span>
                                        <span className="text-sm">
                                        {section.label}
                                        </span>
                                    </button>
                                    );
                                })}
                            </nav>
                        </div>
                        
                        <div className="mt-auto p-4 rounded-xl bg-surface/50 border border-border-color">
                            <p className="text-xs text-slate-400 leading-relaxed mb-3">Questions about these terms or our legal standing?</p>
                            <button className="w-full py-2 bg-surface hover:bg-border-color border border-border-color text-xs font-bold rounded-lg transition-colors">Legal Support</button>
                        </div>
                    </div>
                </aside>
                <div className="flex-1 overflow-y-auto content-scroll">
                    <div className="max-w-[850px] mx-auto px-6 py-12 lg:px-12 lg:py-16">
                        <div className="mb-12 border-b border-border-color pb-8">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold uppercase tracking-wider rounded-full border border-primary/30">Contractual</span>
                                <span className="text-slate-500 text-sm">•</span>
                                <span className="text-slate-500 text-sm">Last Updated: February 09, 2026</span>
                            </div>
                            <h1 className="text-5xl font-black text-white tracking-tight mb-4 leading-tight">Terms of Service</h1>
                            <p className="text-slate-400 text-lg leading-relaxed max-w-2xl">
                                Please read these Terms of Service carefully before using the ACCLiveTelemetry platform and local synchronization software.
                            </p>
                        </div>
                        <section className="mb-16 scroll-mt-24" id="acceptance">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">1. Acceptance of Terms</h2>
                            </div>
                            <div className="space-y-4 text-slate-300 leading-relaxed">
                                <p>By accessing or using ACCLiveTelemetry, you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the service.</p>
                                <p>Our service provides real-time telemetry analysis for Assetto Corsa Competizione. These terms apply to all visitors, users, and others who access or use the service via our web portal or the local sync client.</p>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="user-accounts">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">2. User Accounts</h2>
                            </div>
                            <div className="bg-surface rounded-xl p-6 border border-border-color mb-6">
                                <h3 className="text-primary font-bold mb-3 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-sm">key</span>
                                    Steam Account Integration
                                </h3>
                                <p className="text-slate-300 mb-4">Registration requires a valid Steam account. We utilize Steam OpenID for authentication. By linking your account, you acknowledge:</p>
                                <ul className="list-disc pl-5 space-y-2 text-slate-400 text-sm">
                                    <li>You are responsible for maintaining the security of your Steam account.</li>
                                    <li>You must not use the service for any illegal or unauthorized purpose.</li>
                                    <li>We reserve the right to suspend accounts that violate the community standards or competitive integrity of the platform.</li>
                                </ul>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="license">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">3. Telemetry &amp; Software License</h2>
                            </div>
                            <div className="space-y-6">
                                <p className="text-slate-300">ACCLiveTelemetry grants you a personal, non-exclusive, non-transferable license to use our local sync client for the sole purpose of transmitting telemetry data from your game instance to our servers.</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-surface/40 p-5 rounded-xl border border-border-color/50">
                                        <div className="text-white font-bold mb-2 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-primary">sync</span>
                                            Local Sync Client
                                        </div>
                                        <p className="text-sm text-slate-400">The client must not be modified, reverse-engineered, or used to inject malicious data into the platform.</p>
                                    </div>
                                    <div className="bg-surface/40 p-5 rounded-xl border border-border-color/50">
                                        <div className="text-white font-bold mb-2 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-primary">groups</span>
                                            Race Engineer Sharing
                                        </div>
                                        <p className="text-sm text-slate-400">Our 'Race Engineer' feature allows data sharing with designated teammates. You control who sees your data.</p>
                                    </div>
                                </div>
                                <div className="bg-primary/10 border-l-4 border-primary p-6 rounded-r-xl">
                                    <h4 className="text-white font-bold mb-2">Data Ownership &amp; Usage</h4>
                                    <p className="text-sm text-slate-300">While you own your individual racing data, by using the service you grant ACCLiveTelemetry a license to use anonymized telemetry for global leaderboards, aggregate performance metrics, and platform optimization.</p>
                                </div>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="payments">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">4. Subscription &amp; Payments</h2>
                            </div>
                            <div className="prose prose-invert prose-slate max-w-none text-slate-300">
                                <p>Certain premium features of ACCLiveTelemetry require a paid subscription. All payments are processed through secure third-party payment providers.</p>
                                <div className="mt-4 p-4 bg-surface border border-border-color rounded-xl">
                                    <div className="flex items-start gap-3">
                                        <span className="material-symbols-outlined text-primary mt-1">info</span>
                                        <div className="text-sm text-slate-400">
                                            <p className="mb-2"><strong className="text-white">Billing:</strong> Subscriptions are billed in advance on a recurring basis. You may cancel at any time.</p>
                                            <p><strong className="text-white">Refunds:</strong> Refund requests are handled on a case-by-case basis within 14 days of the initial purchase or renewal.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="conduct">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">5. Prohibited Conduct</h2>
                            </div>
                            <p className="text-slate-300 mb-6">To maintain the integrity of our leaderboards and platform, users are prohibited from:</p>
                            <div className="space-y-4">
                                <div className="flex gap-4 items-start">
                                    <span className="size-6 rounded-full bg-border-color flex items-center justify-center text-xs font-bold text-white shrink-0 mt-1">1</span>
                                    <p className="text-slate-400"><strong className="text-white">Data Manipulation:</strong> Using external tools to spoof telemetry data or falsify lap times.</p>
                                </div>
                                <div className="flex gap-4 items-start">
                                    <span className="size-6 rounded-full bg-border-color flex items-center justify-center text-xs font-bold text-white shrink-0 mt-1">2</span>
                                    <p className="text-slate-400"><strong className="text-white">API Abuse:</strong> Excessive scraping or automated querying of our public data without prior authorization.</p>
                                </div>
                                <div className="flex gap-4 items-start">
                                    <span className="size-6 rounded-full bg-border-color flex items-center justify-center text-xs font-bold text-white shrink-0 mt-1">3</span>
                                    <p className="text-slate-400"><strong className="text-white">Harassment:</strong> Engaging in abusive behavior within the 'Race Engineer' chat or community hubs.</p>
                                </div>
                            </div>
                        </section>
                        <section className="mb-16 scroll-mt-24" id="liability">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-1 bg-primary rounded-full"></div>
                                <h2 className="text-2xl font-bold text-white tracking-tight">6. Limitation of Liability</h2>
                            </div>
                            <div className="bg-surface border border-border-color rounded-xl p-6">
                                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                    ACCLiveTelemetry IS PROVIDED "AS IS" AND "AS AVAILABLE". IN NO EVENT SHALL OUR TEAM BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                                </p>
                                <ul className="space-y-3">
                                    <li className="flex items-center gap-3 text-xs text-slate-500">
                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                        Your access to or use of (or inability to access or use) the Service.
                                    </li>
                                    <li className="flex items-center gap-3 text-xs text-slate-500">
                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                        Any conduct or content of any third party on the Service.
                                    </li>
                                    <li className="flex items-center gap-3 text-xs text-slate-500">
                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                        Any content obtained from the Service.
                                    </li>
                                </ul>
                            </div>
                        </section>
                        <div className="mt-20 p-10 rounded-2xl bg-gradient-to-br from-surface to-background-dark border border-border-color text-center relative overflow-hidden">
                            <div className="absolute -right-10 -top-10 text-primary/10 opacity-20 rotate-12">
                                <span className="material-symbols-outlined" style={{fontSize: "200px"}}>description</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Questions about these Terms?</h3>
                            <p className="text-slate-400 mb-8 max-w-md mx-auto">Our legal support team is available to clarify any points regarding our software license or data usage policies.</p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <button className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-8 rounded-lg transition-all shadow-lg shadow-primary/20">Contact Legal Support</button>
                                <button className="bg-surface hover:bg-border-color text-white border border-border-color font-bold py-3 px-8 rounded-lg transition-all">Download PDF Terms</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default TermsOfServiceMain;